import { useEffect, useMemo, useState } from 'react';
import {
  pickActiveCoachmark,
  readDismissedCoachmarks,
  writeDismissedCoachmarks,
  type CoachmarkId,
} from './coachmarkState';
import './Coachmarks.css';

/**
 * Lightweight, kid-facing coachmarks (in-app guide).
 *
 * The studio guides a first-time kid through the four-step happy path:
 *  1. Add a class
 *  2. Capture some samples
 *  3. Train the model
 *  4. Test what it learned
 *
 * Each tip is dismissable and remembered in localStorage so the next
 * visit is uncluttered. The active step is derived from project state
 * (class count, sample count, trained state) — the kid never sees a
 * tip that points at something they've already done. Respects
 * `prefers-reduced-motion` (no slide-in animation when set).
 */

export interface CoachmarksProps {
  classCount: number;
  sampleCount: number;
  trained: boolean;
  /** Bumped whenever the kid actually opens the test panel. */
  testedAt: number | null;
}

interface TipDef {
  id: CoachmarkId;
  /** Where on the page to anchor the tip — `selector` is a CSS selector
   *  that's resolved at render-time. Falls back to a floating bottom
   *  card if the selector misses. */
  selector: string;
  title: string;
  body: string;
}

const TIPS: TipDef[] = [
  {
    id: 'first-class',
    selector: '#bl-add-class',
    title: 'Start by adding a class',
    body: 'A class is one thing the model can learn. For example: "me waving" or "a clap". Type a name and click Add.',
  },
  {
    id: 'capture-samples',
    selector: '.bl-card-class__capture-button',
    title: 'Show the model what it looks like',
    body: 'Record a few samples for each class. The more variety the better. Try different angles, different lighting, and different moments.',
  },
  {
    id: 'train-model',
    selector: '.bl-train__button',
    title: 'Now train the model',
    body: 'Once each class has its minimum samples, click Train. It only takes a few seconds.',
  },
  {
    id: 'test-model',
    selector: '.bl-train__button--test',
    title: 'See what it learned',
    body: "Click Test the model. Try things the model has seen before, and things it hasn't. See if it's honest about what it doesn't know.",
  },
];

export function Coachmarks({
  classCount,
  sampleCount,
  trained,
  testedAt,
}: CoachmarksProps): JSX.Element | null {
  const [dismissed, setDismissed] = useState<Set<CoachmarkId>>(() => readDismissedCoachmarks());
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const active = useMemo(
    () => pickActiveCoachmark({ classCount, sampleCount, trained, testedAt }, dismissed),
    [classCount, sampleCount, trained, testedAt, dismissed],
  );
  const tip = active ? TIPS.find((t) => t.id === active) : null;

  // Resolve the anchor element each time the active tip changes, and on
  // window resize / scroll so the bubble follows whatever it's pointing
  // at. Cheap because the work runs once per state change, not per
  // frame.
  useEffect(() => {
    if (!tip) {
      setAnchorRect(null);
      return;
    }
    const measure = (): void => {
      const target = document.querySelector(tip.selector);
      if (!target) {
        setAnchorRect(null);
        return;
      }
      setAnchorRect(target.getBoundingClientRect());
    };
    measure();
    const onResize = (): void => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    // Re-measure on next paint to catch any late layout shift.
    const handle = window.setTimeout(measure, 100);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      window.clearTimeout(handle);
    };
  }, [tip]);

  if (!tip) return null;

  const dismiss = (): void => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(tip.id);
      writeDismissedCoachmarks(next);
      return next;
    });
  };

  // Position the bubble near the target if we found one; otherwise
  // anchor to the bottom-right of the viewport as a graceful fallback.
  // When anchored, also compute an arrow x-offset (relative to the
  // bubble's left edge) so the ::before triangle points back at the
  // anchored element's horizontal center. Clamped a few px in from
  // each edge so the arrow never lands on a rounded corner.
  const BUBBLE_MAX_W = 360;
  const ARROW_EDGE_PAD = 18;

  let style: React.CSSProperties;
  let arrowX: number | null = null;
  if (anchorRect) {
    const bubbleW = Math.min(BUBBLE_MAX_W, window.innerWidth - 16);
    const left = Math.max(
      8,
      Math.min(
        window.innerWidth - bubbleW - 8,
        anchorRect.left + anchorRect.width / 2 - bubbleW / 2,
      ),
    );
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    arrowX = Math.max(ARROW_EDGE_PAD, Math.min(bubbleW - ARROW_EDGE_PAD, anchorCenter - left));
    style = {
      position: 'fixed',
      top: Math.max(8, anchorRect.bottom + 12),
      left,
      // Custom prop consumed by the ::before triangle in CSS.
      ['--bl-coach-arrow-x' as string]: `${arrowX}px`,
    };
  } else {
    style = {
      position: 'fixed',
      right: 16,
      bottom: 16,
    };
  }

  return (
    <div
      role="dialog"
      aria-label={tip.title}
      aria-describedby={`bl-coachmark-body-${tip.id}`}
      className="bl-coach"
      data-anchored={arrowX !== null ? 'true' : undefined}
      style={style}
    >
      <p className="bl-coach__title">{tip.title}</p>
      <p className="bl-coach__body" id={`bl-coachmark-body-${tip.id}`}>
        {tip.body}
      </p>
      <div className="bl-coach__actions">
        <button type="button" className="bl-coach__got-it" onClick={dismiss}>
          Got it
        </button>
      </div>
    </div>
  );
}
